import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import DataTableToolbar from "./DataTableToolbar";
import DataTableHeader from "./DataTableHeader";
import DataTableBody from "./DataTableBody";
import DataTablePagination from "./DataTablePagination";
import DataTableCards from "./DataTableCards";
import { useIsMobile } from "../chat/useMedia";
import { useControllableState, useDebouncedValue } from "./hooks";
import { cx, filterRows, sortRows } from "./utils";

/**
 * Generic, page-agnostic table. It knows HOW to render rows, never WHAT they are:
 * columns, data, filters, actions, fetching and navigation all come from the parent.
 *
 * @template T
 * @typedef {Object} DataTableColumn
 * @property {string} key  Field to read from each row (dot paths such as "owner.name" work),
 *   or any unique id for computed columns such as "actions".
 * @property {string} label  Header text.
 * @property {boolean} [sortable]
 * @property {string} [width]  CSS width, e.g. "160px" or "20%".
 * @property {"left"|"center"|"right"} [align]
 * @property {(value: unknown, row: T, rowIndex: number) => import("react").ReactNode} [render]
 *   Custom cell. `value` is the resolved cell value (see `accessor`).
 * @property {(row: T) => unknown} [accessor]  Derive the cell value instead of reading `key`.
 * @property {(row: T) => (string|number|boolean|Date|null|undefined)} [sortValue]
 *   Value used for client-side sorting when it differs from the displayed value.
 * @property {boolean} [searchable]  Client-side search: when any column sets `true`, only those
 *   columns are searched; otherwise all columns except ones set to `false`.
 * @property {(row: T) => string} [searchValue]  Text matched by client-side search.
 * @property {boolean} [wrap]  Allow text wrapping (cells don't wrap by default).
 * @property {string} [headerClassName]
 * @property {string} [cellClassName]
 */

/**
 * @typedef {Object} DataTableSort
 * @property {string} key
 * @property {"asc"|"desc"} direction
 */

/** Default UI copy. Pass `labels` to override any of it (e.g. for translation). */
export const DEFAULT_LABELS = {
  search: "Search",
  clearSearch: "Clear search",
  filters: "Filters",
  empty: "No results found",
  error: "Something went wrong while loading data.",
  selectAll: "Select all rows on this page",
  selectRow: (rowLabel) => `Select ${rowLabel}`,
  rowsPerPage: "Rows per page",
  previous: "Previous",
  next: "Next",
  previousPage: "Previous page",
  nextPage: "Next page",
  page: (n) => `Page ${n}`,
  pageOf: (page, count) => `Page ${page} of ${count}`,
  pagination: "Pagination",
  range: (start, end, total) => (total === 0 ? "0 results" : `${start}–${end} of ${total}`),
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const defaultRowId = (row, index) => {
  const id = row && typeof row === "object" ? row.id : undefined;
  return typeof id === "string" || typeof id === "number" ? id : index;
};

/**
 * @template T
 * @param {Object} props
 * @param {DataTableColumn<T>[]} props.columns
 * @param {T[]} props.data  Rows to show. With server-side pagination this is just the current page.
 * @param {(row: T, index: number) => (string|number)} [props.getRowId]  Stable row id
 *   (defaults to `row.id`, falling back to the row index). Needed for reliable selection.
 * @param {string} [props.ariaLabel]  Accessible name for the table.
 * @param {string} [props.className]
 * @param {string} [props.maxHeight]  Scroll the body vertically with a sticky header, e.g. "480px".
 *
 * @param {DataTableSort|null} [props.sort]  Controlled sort (null = unsorted).
 * @param {DataTableSort|null} [props.defaultSort]
 * @param {(sort: DataTableSort|null) => void} [props.onSortChange]
 * @param {boolean} [props.manualSorting]  Server-side sorting: `data` is shown in the order given.
 *
 * @param {boolean} [props.searchable]
 * @param {string} [props.searchPlaceholder]
 * @param {string} [props.defaultSearchValue]
 * @param {(value: string) => void} [props.onSearchChange]  Called with the debounced value.
 * @param {number} [props.searchDebounceMs=300]
 * @param {boolean} [props.manualFiltering]  Server-side search: `data` is not filtered locally.
 *
 * @param {boolean} [props.filterable]
 * @param {import("react").ReactNode} [props.filterContent]  The page's own filter controls.
 * @param {number} [props.activeFilterCount]  Badge on the filter button.
 * @param {boolean} [props.defaultFiltersOpen]
 *
 * @param {boolean} [props.pagination]
 * @param {number} [props.page]  Controlled page (1-based).
 * @param {number} [props.pageSize]  Controlled page size.
 * @param {number} [props.defaultPageSize=10]
 * @param {number[]} [props.pageSizeOptions]
 * @param {number} [props.totalItems]  Total across all pages. Providing it turns on server-side
 *   pagination unless `manualPagination` says otherwise.
 * @param {boolean} [props.manualPagination]
 * @param {(page: number) => void} [props.onPageChange]
 * @param {(pageSize: number) => void} [props.onPageSizeChange]
 * @param {boolean} [props.hidePageSizeSelector]
 *
 * @param {boolean} [props.loading]
 * @param {number} [props.loadingRowCount=5]
 * @param {import("react").ReactNode} [props.emptyState]
 * @param {import("react").ReactNode} [props.error]  Truthy shows the error state; a string is used as the message.
 * @param {import("react").ReactNode} [props.errorState]
 *
 * @param {boolean} [props.selectable]
 * @param {(string|number)[]} [props.selectedRows]  Controlled selected row ids.
 * @param {(string|number)[]} [props.defaultSelectedRows]
 * @param {(ids: (string|number)[], rows: T[]) => void} [props.onSelectionChange]
 * @param {(row: T) => boolean} [props.isRowSelectable]
 * @param {(row: T, index: number) => string} [props.getRowAriaLabel]  Names a row for its checkbox.
 *
 * @param {(row: T, index: number) => void} [props.onRowClick]
 * @param {string|((row: T, index: number) => string)} [props.rowClassName]
 *
 * @param {import("react").ReactNode} [props.toolbarLeft]
 * @param {import("react").ReactNode} [props.toolbarRight]
 * @param {Partial<typeof DEFAULT_LABELS>} [props.labels]
 * @param {"cards"|"table"} [props.mobileLayout="cards"]  Phones: rows as a stacked list (first column
 *   as the title, actions beside it) instead of a sideways-scrolling table.
 */
export default function DataTable({
  columns,
  data,
  getRowId = defaultRowId,
  ariaLabel,
  className,
  maxHeight,

  sort: sortProp,
  defaultSort = null,
  onSortChange,
  manualSorting = false,

  searchable = false,
  searchPlaceholder,
  defaultSearchValue = "",
  onSearchChange,
  searchDebounceMs = 300,
  manualFiltering = false,

  filterable = false,
  filterContent,
  activeFilterCount = 0,
  defaultFiltersOpen = false,

  pagination = false,
  page: pageProp,
  pageSize: pageSizeProp,
  defaultPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  totalItems,
  manualPagination = totalItems !== undefined,
  onPageChange,
  onPageSizeChange,
  hidePageSizeSelector = false,

  loading = false,
  loadingRowCount = 5,
  emptyState,
  error,
  errorState,

  selectable = false,
  selectedRows,
  defaultSelectedRows = [],
  onSelectionChange,
  isRowSelectable,
  getRowAriaLabel,

  onRowClick,
  rowClassName,

  toolbarLeft,
  toolbarRight,
  labels: labelOverrides,
  mobileLayout = "cards",
}) {
  const cards = useIsMobile() && mobileLayout === "cards";
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelOverrides }), [labelOverrides]);
  const uid = useId();

  const [sort, setSort] = useControllableState(sortProp, defaultSort, onSortChange);
  const [page, setPage] = useControllableState(pageProp, 1, onPageChange);
  const [pageSize, setPageSize] = useControllableState(pageSizeProp, defaultPageSize, onPageSizeChange);
  const [selectedKeys, setSelectedKeys] = useControllableState(selectedRows, defaultSelectedRows);
  const [filtersOpen, setFiltersOpen] = useState(defaultFiltersOpen);

  // --- Search: the input updates immediately, the applied query is debounced.
  const [searchInput, setSearchInput] = useState(defaultSearchValue);
  const appliedSearch = useDebouncedValue(searchInput, searchDebounceMs);
  const lastAppliedSearch = useRef(appliedSearch);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    if (lastAppliedSearch.current === appliedSearch) return;
    lastAppliedSearch.current = appliedSearch;
    onSearchChangeRef.current?.(appliedSearch);
    if (pagination) setPage(1);
  }, [appliedSearch, pagination, setPage]);

  // --- Row ids (stable across sorting/filtering because they come from `data`).
  const rowKeys = useMemo(() => {
    const map = new Map();
    rows.forEach((row, index) => map.set(row, getRowId(row, index)));
    return map;
  }, [rows, getRowId]);
  const getRowKey = useCallback((row) => rowKeys.get(row), [rowKeys]);

  // --- Client-side pipeline: search → sort → paginate (each step skippable for server-side).
  const searched = useMemo(
    () => (searchable && !manualFiltering ? filterRows(rows, columns, appliedSearch) : rows),
    [rows, columns, appliedSearch, searchable, manualFiltering],
  );

  const sorted = useMemo(() => {
    if (manualSorting || !sort) return searched;
    const column = columns.find((c) => c.key === sort.key);
    return column ? sortRows(searched, column, sort.direction) : searched;
  }, [searched, sort, columns, manualSorting]);

  const total = pagination && manualPagination ? totalItems ?? rows.length : sorted.length;
  const pageCount = pagination ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const currentPage = Math.min(Math.max(1, page), pageCount);

  const pageRows = useMemo(() => {
    if (!pagination || manualPagination) return sorted;
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, pagination, manualPagination, currentPage, pageSize]);

  // Keep the page in range when the data shrinks (e.g. after a delete or search).
  useEffect(() => {
    if (pagination && !loading && page > pageCount) setPage(pageCount);
  }, [pagination, loading, page, pageCount, setPage]);

  const handleSort = (key) => {
    // none → ascending → descending → none
    const next =
      sort?.key !== key
        ? { key, direction: "asc" }
        : sort.direction === "asc"
          ? { key, direction: "desc" }
          : null;
    setSort(next);
    if (pagination) setPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  // --- Selection (ids, so it survives re-renders and refetches).
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const updateSelection = (keys) => {
    setSelectedKeys(keys);
    onSelectionChange?.(keys, rows.filter((row) => keys.includes(getRowKey(row))));
  };

  const selectableKeysOnPage = pageRows
    .filter((row) => !isRowSelectable || isRowSelectable(row))
    .map(getRowKey);
  const selectedOnPage = selectableKeysOnPage.filter((key) => selectedSet.has(key)).length;
  const allSelected = selectableKeysOnPage.length > 0 && selectedOnPage === selectableKeysOnPage.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      const onPage = new Set(selectableKeysOnPage);
      updateSelection(selectedKeys.filter((key) => !onPage.has(key)));
    } else {
      updateSelection([...selectedKeys, ...selectableKeysOnPage.filter((key) => !selectedSet.has(key))]);
    }
  };

  const toggleRow = (key) =>
    updateSelection(selectedSet.has(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]);

  const showToolbar = searchable || filterable || toolbarLeft || toolbarRight;
  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className={cx("overflow-hidden rounded-xl border border-app-primary bg-app-card shadow-sm", className)}>
      {showToolbar && (
        <DataTableToolbar
          searchable={searchable}
          searchInput={searchInput}
          onSearchInput={setSearchInput}
          searchPlaceholder={searchPlaceholder}
          filterable={filterable}
          filterContent={filterContent}
          filtersOpen={filtersOpen}
          onToggleFilters={() => setFiltersOpen((open) => !open)}
          filterPanelId={`${uid}-filters`}
          activeFilterCount={activeFilterCount}
          toolbarLeft={toolbarLeft}
          toolbarRight={toolbarRight}
          labels={labels}
        />
      )}

      {cards ? (
        <div style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
          <DataTableCards
            columns={columns}
            rows={pageRows}
            getRowKey={getRowKey}
            loading={loading}
            loadingRowCount={loadingRowCount}
            error={error}
            errorState={errorState}
            emptyState={emptyState}
            selectable={selectable}
            selectedSet={selectedSet}
            isRowSelectable={isRowSelectable}
            onToggleRow={toggleRow}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={toggleAll}
            selectAllDisabled={loading || selectableKeysOnPage.length === 0}
            getRowAriaLabel={getRowAriaLabel}
            onRowClick={onRowClick}
            rowClassName={rowClassName}
            rowOffset={pagination && !manualPagination ? (currentPage - 1) * pageSize : 0}
            sort={sort}
            onSortChange={(next) => {
              setSort(next);
              if (pagination) setPage(1);
            }}
            labels={labels}
          />
        </div>
      ) : (
      <div className="overflow-x-auto" style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
        <table className="min-w-full border-collapse" aria-label={ariaLabel} aria-busy={loading || undefined}>
          <DataTableHeader
            columns={columns}
            sort={sort}
            onSort={handleSort}
            selectable={selectable}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={toggleAll}
            selectAllDisabled={loading || selectableKeysOnPage.length === 0}
            stickyHeader={Boolean(maxHeight)}
            labels={labels}
          />
          <DataTableBody
            columns={columns}
            rows={pageRows}
            getRowKey={getRowKey}
            colSpan={colSpan}
            loading={loading}
            loadingRowCount={loadingRowCount}
            error={error}
            errorState={errorState}
            emptyState={emptyState}
            selectable={selectable}
            selectedSet={selectedSet}
            isRowSelectable={isRowSelectable}
            onToggleRow={toggleRow}
            getRowAriaLabel={getRowAriaLabel}
            onRowClick={onRowClick}
            rowClassName={rowClassName}
            rowOffset={pagination && !manualPagination ? (currentPage - 1) * pageSize : 0}
            labels={labels}
          />
        </table>
      </div>
      )}

      {pagination && (
        <DataTablePagination
          page={currentPage}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          totalItems={total}
          onPageChange={(p) => setPage(Math.min(Math.max(1, p), pageCount))}
          onPageSizeChange={hidePageSizeSelector ? undefined : handlePageSizeChange}
          disabled={loading}
          pageSizeSelectId={`${uid}-page-size`}
          labels={labels}
        />
      )}
    </div>
  );
}
